import PropTypes from 'prop-types';
import React, { useCallback, memo } from 'react';
import { connect } from 'react-redux';
import { List, Map } from 'immutable';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import {
  handleGetHarvcoLeadSuggestion,
  handleGetClientLeadSuggestion,
  handleGetExecutiveSuggestion,
} from '../../actions/popup';
import AddNewPlatformProjectPopup from '../../components/Popup/AddNewPlatformProjectPopup';
import { usePopupContext } from '../../contexts/PopupContext';

const handleGetSuggestionValue = suggestion => suggestion.text;

const handleRenderSuggestion = suggestion => <div>{suggestion.text}</div>;

const AddNewPlatformProjectContainer = ({
  popup,
  handleGetHarvcoLeadSuggestion,
  handleGetClientLeadSuggestion,
  handleGetExecutiveSuggestion,
}) => {
  const { closePopup, updatePopupFormData, startPopupLoading, endPopupLoading } = usePopupContext();

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;

      updatePopupFormData({
        [name]: value,
      });
    },
    [updatePopupFormData],
  );

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleCreate = useCallback(() => {
    const onCreate = popup.getIn(['props', 'onCreate'], () => {});
    const onSuccessResponse = popup.getIn(['props', 'onSuccessResponse'], () => {});

    onCreate(popup.get('formData'), {
      beforeStart: startPopupLoading,
      afterSuccess: () => {
        endPopupLoading();
        onSuccessResponse();
      },
      afterError: ({ responseBody }) => {
        endPopupLoading(responseBody);
      },
    });
  }, [popup, startPopupLoading, endPopupLoading]);

  const handleUpdateHarvcoLeadSuggestions = useCallback(
    update => {
      updatePopupFormData({
        harvcoLead: update,
      });
    },
    [updatePopupFormData],
  );

  const handleHarvcoLeadSuggestionFetch = useCallback(
    ({ value }) => {
      handleGetHarvcoLeadSuggestion(value, ({ response }) => {
        updatePopupFormData({
          harvcoLead: {
            suggestions: response.data,
          },
        });
      });
    },
    [handleGetHarvcoLeadSuggestion, updatePopupFormData],
  );

  const handleHarvcoLeadSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      updatePopupFormData({
        harvcoLead: {
          text: suggestion.text,
          suggestions: [],
          selected: suggestion,
        },
      });
    },
    [updatePopupFormData],
  );

  const handleUpdateClientLeadSuggestions = useCallback(
    update => {
      updatePopupFormData({
        clientLead: update,
      });
    },
    [updatePopupFormData],
  );

  const handleClientLeadSuggestionFetch = useCallback(
    ({ value }) => {
      const buyerId = popup.getIn(['props', 'buyerId']);

      handleGetClientLeadSuggestion(buyerId, value, ({ response }) => {
        updatePopupFormData({
          clientLead: {
            suggestions: response.data,
          },
        });
      });
    },
    [popup, handleGetClientLeadSuggestion, updatePopupFormData],
  );

  const handleClientLeadSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      updatePopupFormData({
        clientLead: {
          text: suggestion.text,
          suggestions: [],
          selected: suggestion,
        },
      });
    },
    [updatePopupFormData],
  );

  const handleUpdateExecutiveSuggestions = useCallback(
    update => {
      updatePopupFormData({
        executive: update,
      });
    },
    [updatePopupFormData],
  );

  const handleExecutiveSuggestionFetch = useCallback(
    ({ value }) => {
      handleGetExecutiveSuggestion(value, ({ response }) => {
        updatePopupFormData({
          executive: {
            suggestions: response.data,
          },
        });
      });
    },
    [handleGetExecutiveSuggestion, updatePopupFormData],
  );

  const handleExecutiveSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      updatePopupFormData({
        executive: {
          text: suggestion.text,
          suggestions: [],
          selected: suggestion,
        },
      });
    },
    [updatePopupFormData],
  );

  const formData = popup.get('formData');

  const harvcoLeadSuggestionsProps = {
    inputProps: {
      className: 'form-control',
      id: 'harvcoLeadDropdown',
      name: 'harvcoLeadDropdown',
      label: 'HarvCo Lead',
      disabled: false,
      placeholder: '',
    },
    suggestions: popup.getIn(['formData', 'harvcoLead', 'suggestions'], new List()),
    onUpdateSuggestions: handleUpdateHarvcoLeadSuggestions,
    getSuggestion: handleHarvcoLeadSuggestionFetch,
    getSuggestionValue: handleGetSuggestionValue,
    renderSuggestion: handleRenderSuggestion,
    onSuggestionSelected: handleHarvcoLeadSuggestionSelected,
    text: popup.getIn(['formData', 'harvcoLead', 'text'], ''),
  };

  const clientLeadSuggestionsProps = {
    inputProps: {
      className: 'form-control',
      id: 'clientLeadDropdown',
      name: 'clientLeadDropdown',
      label: 'Client Lead',
      disabled: false,
      placeholder: '',
    },
    suggestions: popup.getIn(['formData', 'clientLead', 'suggestions'], new List()),
    onUpdateSuggestions: handleUpdateClientLeadSuggestions,
    getSuggestion: handleClientLeadSuggestionFetch,
    getSuggestionValue: handleGetSuggestionValue,
    renderSuggestion: handleRenderSuggestion,
    onSuggestionSelected: handleClientLeadSuggestionSelected,
    text: popup.getIn(['formData', 'clientLead', 'text'], ''),
  };

  const executiveSuggestionsProps = {
    inputProps: {
      className: 'form-control',
      id: 'executiveDropdown',
      name: 'executiveDropdown',
      label: 'Executive',
      disabled: false,
      placeholder: '',
    },
    suggestions: popup.getIn(['formData', 'executive', 'suggestions'], new List()),
    onUpdateSuggestions: handleUpdateExecutiveSuggestions,
    getSuggestion: handleExecutiveSuggestionFetch,
    getSuggestionValue: handleGetSuggestionValue,
    renderSuggestion: handleRenderSuggestion,
    onSuggestionSelected: handleExecutiveSuggestionSelected,
    text: popup.getIn(['formData', 'executive', 'text'], ''),
  };

  return (
    <AddNewPlatformProjectPopup
      clientLeadSuggestions={clientLeadSuggestionsProps}
      executiveSuggestions={executiveSuggestionsProps}
      formData={formData}
      harvcoLeadSuggestions={harvcoLeadSuggestionsProps}
      isPopupFetching={popup.get('isPopupFetching')}
      isPopupFetchingError={popup.get('isPopupFetchingError')}
      onChange={handleChange}
      onClose={handleClose}
      onCreate={handleCreate}
    />
  );
};

AddNewPlatformProjectContainer.propTypes = {
  popup: PropTypes.instanceOf(Map).isRequired,
};

export { AddNewPlatformProjectContainer };
export default connect(
  (state, props) => ({
    ...props,
  }),
  {
    handleGetHarvcoLeadSuggestion,
    handleGetClientLeadSuggestion,
    handleGetExecutiveSuggestion,
  },
  mergeProps,
  connectOptions,
)(memo(AddNewPlatformProjectContainer));
